import React, {memo, ReactNode, useCallback, useEffect, useMemo, useState} from 'react'
import Language from "language"
import styled from "styled-components"
import {
  AdministrationLayout,
  ContentContainer,
  IconContainer,
  InformationsContainer,
  ContentCard,
  TableLink,
  JustifyContentProperty,
  BironTable
} from "@biron-data/react-components"
import {Roles, UAC, User} from "./UserManager.types";
import {Checkbox, Col, notification, Row, Select, TableColumnProps} from "antd";
import {useNavigate, useParams} from "react-router-dom";
import {useSelector} from "react-redux";
import {isDatamodelRole, isWorkspaceRole} from "./UserManager.utils";
import DatamodelRights from "./DatamodelRights";
import {getAvailablesForAce, getCurrentWorkspace} from "../../../redux/workspace.selector";
import {ChevronDownIcon, PencilIcon} from "@heroicons/react/outline";
import {getCurrentEnvironmentId, getDataModels} from "../../../redux/environment.selector";
import {ChevronRightIcon} from "@heroicons/react/solid";
import WorkspaceRights from "./WorkspaceRights";
import WorkspaceRightUpdater from "./WorkspaceRightUpdater";
import {Submenu} from "@biron-data/icons";
import DatamodelRightUpdater from "./DatamodelRightUpdater";
import useDispatch from "../../../hooks/useDispatch";
import {
  buildWorkspaceAdminDatamodelUri, buildWorkspaceAdminUsersUri,
  buildWorkspaceAdminWorkspaceUri
} from "../../../commons/buildWorkspaceAdminUri";
import {getLocales} from "../../../commons/locales/getLocales";
import {
  useDatamodelsWithWorkspace,
  useGetDatamodelRolesSynthesis,
  useGetEnvironmentRolesSynthesis,
  useGetRolesSynthesis,
  useUpdateDatamodelRights,
  useUpdateEnvironmentRights,
  useUpdateWorkspaceRights
} from "./UserDetails.hooks";
import {ClientWorkspace} from "../../../redux/models/appEnvironment";
import AdminBreadcrumb from "../Admin.Breadcrumb";
import {useGetDatamodelRightsGlobalPopover} from "./DatamodelRightsGlobalPopover";
import {getIsDatamodelManager, getIsDatamodelViewer} from "./DatamodelRights.utils";
import {useGetWorkspaceRightsGlobalPopover} from './WorkspaceRightsGlobalPopover';
import {getIsWorkspaceManager, getIsWorkspaceReader, getIsWorkspaceWriter} from "./WorkspaceRights.utils";
import UserDetailsDescription from "./UserDetails.Description";
import EnvironmentRightUpdater from "./EnvironmentRightUpdater";
import {useGetIsCurrentUser, useHandleActiveChange} from "./UserManager.hooks";
import useUACWithAces, {UACWithAces} from "../../../hooks/useUACWithAces";
import {getAcesSelector, getAssocClient} from "../../../redux/user.selector";
import {useGetPermUserCanEdit} from "../../../hooks/usePermUserCanEdit";
import UserForm from "./UserForm";
import {buildAndBatch} from "../../../commons/batch";
import {useHandleResetPassword} from "../../../hooks/useHandleResetPassword";

interface Props {
}

interface DataType {
  roles: Roles[] | undefined
  workspaces: (Pick<ClientWorkspace, "id" | "name"> & {
    roles: Roles[] | undefined
  })[]
  code: string
  alias: string
  description?: string | undefined
  includedMetrics?: any
  includedDimensions?: any
}

const UserDetails = memo<Props>(function UserDetails() {
  const params = useParams()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const {uid} = params
  const assocClient = useSelector(getAssocClient)
  const workspace = useSelector(getCurrentWorkspace)
  const aces = useSelector(getAcesSelector)
  const availableWorkspacesForAce = useSelector(getAvailablesForAce)
  const [uacWithAces]: (UACWithAces | undefined)[] = useUACWithAces(assocClient && aces ? [assocClient] : [], aces ? [aces] : [])
  const getIsCurrentUser = useGetIsCurrentUser()
  const [editedUac, setEditedUac] = useState<UAC>()

  const datamodels = useSelector(getDataModels)
  const environmentId = useSelector(getCurrentEnvironmentId)
  const [loading, setLoading] = useState(true)

  const permUserCanEdit = useGetPermUserCanEdit()(uacWithAces?.user)

  useEffect(() => {
    if (uid) {
      dispatch.user.loadAssocClient(uid).then(user => {
        if (user){
          dispatch.user.loadAces({
            userId: Number(uid),
            assocClient: user,
            environmentId
          }).then(() => {
            setLoading(false)
          })
        }
      })
    }
  }, [dispatch, environmentId, uid])

  const datamodelsWithWorkspace = useDatamodelsWithWorkspace(
    datamodels,
    uacWithAces?.user.aces,
    availableWorkspacesForAce,
  )

  const getRolesSynthesis = useGetRolesSynthesis()
  const getDatamodelRolesSynthesis = useGetDatamodelRolesSynthesis()
  const getEnvironmentRolesSynthesis = useGetEnvironmentRolesSynthesis()

  const onUpdateWorkspaceRights = useUpdateWorkspaceRights(
    uacWithAces?.user.id,
    uacWithAces?.user.aces,
    dispatch.user.updateUserAces)

  const onUpdateDatamodelRights = useUpdateDatamodelRights(
    environmentId,
    availableWorkspacesForAce,
    dispatch.user.updateUserAces)

  const onUpdateEnvironmentRights = useUpdateEnvironmentRights(
    uacWithAces?.user.id,
    uacWithAces?.user.aces,
    dispatch.user.updateUserAces
  )

  const handleActiveChange = useHandleActiveChange(
    dispatch.user.updateAssocClientActive
  )

  const datamodelPopoverContent = useGetDatamodelRightsGlobalPopover()
  const workspacePopoverContent = useGetWorkspaceRightsGlobalPopover()

  const columns: TableColumnProps<DataType>[] = useMemo(() => ([
    {
      title: Language.get('admin.user.columns.name'),
      dataIndex: 'alias',
      width: "40%",
      render: (a, b) => <TableLink link={workspace ? buildWorkspaceAdminDatamodelUri(workspace, b.code) : ""}>{b.alias}</TableLink>
    },
    {
      title: Language.get('admin.user.columns.access'),
      width: "45%",
      render: (a, d) => <DatamodelRightUpdater
        roles={d.roles?.filter(isDatamodelRole) ?? []}
        isDisabled={!uacWithAces?.active}
        onUpdate={(newRoles) => {
        setLoading(true)
        onUpdateDatamodelRights(uacWithAces?.user.id, uacWithAces?.user.aces, newRoles, d.code)?.then(() => {
          setLoading(false)
        }).catch(() => setLoading(false))
      }} />
    },
    {
      title: Language.get('admin.user.columns.rights'),
      width: "15%",
      render: (_, d) => {
        const roles = d.roles?.filter(isDatamodelRole) ?? []
        const isDatamodelManager = getIsDatamodelManager(roles)
        const isDatamodelViewer = getIsDatamodelViewer(roles)

        return <DatamodelRights
          roles={roles}
          layout={'row'}
          justifyContent={JustifyContentProperty.end}
          tooltipPlacement={"left"}
          popover={datamodelPopoverContent(
            d,
            isDatamodelViewer,
            isDatamodelManager
          )}/>
      },
    },
  ]), [onUpdateDatamodelRights, datamodelPopoverContent, uacWithAces, workspace])

  const handleUserFormConfirm = useCallback(
    (newUserData: User) => {
      return dispatch.user.updateUser({
            id: newUserData.id,
            data: newUserData,
          }).then(() => {
            setEditedUac(undefined)
        notification.info({
          message: Language.get('saved-action'),
          placement: 'bottomRight',
          duration: 2.5,
        })
      })
    }
    , [dispatch])

  const isUserEditionDisabled = useMemo(() => !permUserCanEdit || loading, [loading, permUserCanEdit])

  const handleResetPassword = useHandleResetPassword(
    setEditedUac,
    dispatch.users.resetPassword
  )

  const handleUserFormCancel = useCallback(() => {
    setEditedUac(undefined)
  }, [])

  const handleDelete = useCallback((uac: { id: string | number }) => {
    dispatch.users.deleteAssocClient({
      forBatch: true,
      uac,
    }).then(() => {
      if (workspace) {
        navigate(buildWorkspaceAdminUsersUri(workspace))
      }
      setEditedUac(undefined)
    })
  }, [dispatch.users, navigate, workspace])

  return <>
    <UserForm {...{
      uac: editedUac,
      onActiveChange: handleActiveChange,
      onResetPassword: handleResetPassword,
      onDelete: handleDelete,
      onConfirm: handleUserFormConfirm,
      onCancel: handleUserFormCancel,
    }}/>
    {uacWithAces && <AdministrationLayout breadcrumb={
      <AdminBreadcrumb
        category={{
          title: Language.get('admin.user.breadcrumb.title'),
          link: workspace ? buildWorkspaceAdminUsersUri(workspace) : undefined
        }}
        detail={{
          title: uacWithAces?.user.fullName ?? "",
        }}/>
    }>
        <InformationsContainer>
            <ContentCard title={<FlexDivWithGap>
              {isUserEditionDisabled ? <></> : <IconContainer
                clickable={true}
                onClick={() => {
                  setEditedUac(uacWithAces)
                }}
              ><PencilIcon/></IconContainer>}
              {uacWithAces?.user.fullName}
            </FlexDivWithGap>}>
                <FlexContainer gap={20}>
                    <FlexContainer gap={10}>
                        <Row>
                            <Col span={8}>
                                <span>{Language.get("admin.user.content.email")}</span>
                            </Col>
                            <Col span={16}>
                                <span>{uacWithAces?.user.email}</span>
                            </Col>
                        </Row>
                        <Row>
                            <Col span={8}>
                                <span>{Language.get("admin.user.content.statut")}</span>
                            </Col>
                            <Col span={16}>
                                <EnvironmentRightUpdater
                                    roles={uacWithAces?.user.aces ? getEnvironmentRolesSynthesis(uacWithAces?.user.aces) : []}
                                    active={uacWithAces?.active}
                                    isBironUser={uacWithAces?.user.bironUser}
                                    isCurrentUser={getIsCurrentUser(uacWithAces?.user)}
                                    disabled={isUserEditionDisabled}
                                    onUpdate={(newRoles) => onUpdateEnvironmentRights(newRoles, environmentId)}
                                    onUpdateActive={(isActive) => handleActiveChange(uacWithAces, isActive, false)}/>
                            </Col>
                        </Row>
                    </FlexContainer>

                    <Row>
                        <Title>
                            <span>{Language.get("admin.user.content.settings")}</span>
                        </Title>
                    </Row>
                    <FlexContainer gap={10}>
                        <Row>
                            <Col span={8}>
                                <span>{Language.get("admin.user.content.ticketAccess")}</span>
                            </Col>
                            <Col span={16}>
                            <span>
                                <Checkbox
                                    checked={uacWithAces?.user.powerUser}
                                    disabled={isUserEditionDisabled}
                                    onChange={(state) => handleUserFormConfirm({
                                      ...uacWithAces?.user,
                                      powerUser: state.target.checked
                                    })}/>
                            </span>
                            </Col>
                        </Row>
                        <Row>
                            <Col span={8}>
                                <span>{Language.get("admin.user.content.language.title")}</span>
                            </Col>
                            <Col span={16}>
                            <span>
                              <Select
                                  value={uacWithAces?.user.locale}
                                  options={getLocales()}
                                  disabled={isUserEditionDisabled}
                                  onChange={(state) => handleUserFormConfirm({
                                    ...uacWithAces?.user,
                                    locale: state
                                  })}/>
                            </span>
                            </Col>
                        </Row>
                    </FlexContainer>
                    <Row>
                        <Title>
                            <span>{Language.get("admin.user.content.rights")}</span>
                        </Title>
                    </Row>
                    <Row>
                        <FlexContainer gap={20}>
                            <DatamodelRights
                                roles={getDatamodelRolesSynthesis(uacWithAces?.user.aces)}
                                layout={'column'} tooltipPlacement={"right"}/>
                            <WorkspaceRights
                                roles={getRolesSynthesis(uacWithAces?.user.aces)}
                                layout={'column'}
                                tooltipPlacement={"right"}/>
                        </FlexContainer>
                    </Row>
                </FlexContainer>
            </ContentCard>
        </InformationsContainer>
        <ContentContainer>
            <ContentCard title={Language.get("admin.user.content.userRights")} actions={[
              <UserDetailsDescription key={"UserDetailsDescription"}/>
            ]}>
                <BironTable
                    columns={columns}
                    loading={loading}
                    useVirtualGrid={false}
                    expandable={{
                      defaultExpandAllRows: true,
                      expandedRowRender: (record): ReactNode[] => record.workspaces.map((w, index) => {
                        const roles = [...(record.roles ?? []).filter(isDatamodelRole), ...(w.roles ?? []),]
                        const workspaceRoles = roles.filter(isWorkspaceRole) ?? []
                        const isWorkspaceReader = getIsWorkspaceReader(workspaceRoles)
                        const isWorkspaceWriter = getIsWorkspaceWriter(workspaceRoles)
                        const isWorkspaceManager = getIsWorkspaceManager(workspaceRoles)
                        const isDatamodelExplorer = getIsDatamodelManager(record.roles?.filter(isDatamodelRole) ?? [])

                        return <RowWithCenteredContent
                          key={w.id}
                          $hasBottomPadding={index !== (record.workspaces.length - 1)}
                        >
                          <Col span={11}>
                            <FlexDivWithPaddingLeft>
                              <div style={{marginRight: 10}}><Submenu/></div>
                              <TableLink
                                link={workspace ? buildWorkspaceAdminWorkspaceUri(workspace, `${w.id}`) : ''}>{w.name}</TableLink>
                            </FlexDivWithPaddingLeft>
                          </Col>
                          <Col span={10}>
                            <WorkspaceRightUpdater
                              roles={w.roles?.filter(isWorkspaceRole) ?? []}
                              datamodelRights={record.roles?.filter(isDatamodelRole) ?? []}
                              onUpdate={(newRoles) => {
                                setLoading(true)
                                onUpdateWorkspaceRights(newRoles, w.id)?.then(() => {
                                  setLoading(false)
                                }).catch(() => setLoading(false))
                              }}/>
                          </Col>
                          <Col span={3}>
                            <WorkspaceRights
                              roles={roles}
                              layout={"row"}
                              justifyContent={JustifyContentProperty.end}
                              tooltipPlacement={"left"}
                              popover={workspacePopoverContent(
                                w,
                                isWorkspaceReader,
                                isWorkspaceWriter,
                                isWorkspaceManager,
                                isDatamodelExplorer
                              )}/>
                          </Col>
                        </RowWithCenteredContent>
                      }),
                      rowExpandable: (record) => record.alias !== 'Not Expandable',
                      expandIcon: ({expanded, onExpand, record}) =>
                        expanded ? (
                          <IconContainer onClick={e => onExpand(record, e)}>
                            <ChevronDownIcon/>
                          </IconContainer>
                        ) : (
                          <IconContainer onClick={e => onExpand(record, e)}>
                            <ChevronRightIcon/>
                          </IconContainer>
                        )
                    }}
                    dataSource={datamodelsWithWorkspace}
                    size="middle">
                </BironTable>
            </ContentCard>
        </ContentContainer>
    </AdministrationLayout>}
    </>
})

export default UserDetails

const FlexDiv = styled.div`
    display: flex;
`

const FlexDivWithGap = styled(FlexDiv)`
    gap: 5px;
`

const FlexDivWithPaddingLeft = styled(FlexDiv)`
    padding-left: 10%;
`

const RowWithCenteredContent = styled(Row)<{$hasBottomPadding: boolean}>`
    align-items: center;

    ${({$hasBottomPadding}) => $hasBottomPadding ? `padding-bottom: 8px;` : ''}
`

const Title = styled.div`
    font-weight: 500;
    font-size: 20px;
    line-height: 23px;
    letter-spacing: -2%;
`// TODO extract in react-components

const FlexContainer = styled.div<{gap: number}>`
  display: flex;
  flex-direction: column;
    gap:${({gap}) => gap}px;
` // TODO extract in react-components

